import { useImmerAtom } from 'jotai-immer';
import { useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import { first, isDefined, isEmpty, isNullish } from 'remeda';

import { useCreateAutomation, useLoadAutomation, useUpdateAutomation } from '../../../api';

import { useAutomationWizardFormContext } from './automation-wizard-form';
import { automationWizardStateAtom, getAutomationWizardInitialState } from './initial-state';
import { markForDestroy } from './mark-for-destroy';
import type { AutomationWizardStep } from './types';
import { useAutomationActionsController } from './use-automation-actions-controller';
import { useAutomationConditionsController } from './use-automation-conditions-controller';
import { useAutomationSettingsController } from './use-automation-settings-controller';

export function useNavigationController() {
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();

  const form = useAutomationWizardFormContext();
  const automation = form.getValues();

  const [{ step }, setState] = useImmerAtom(automationWizardStateAtom);

  const conditionsController = useAutomationConditionsController();
  const actionsController = useAutomationActionsController();
  const { getNameFieldProps } = useAutomationSettingsController();

  const { createAutomation, isCreateAutomationLoading } = useCreateAutomation();
  const { updateAutomation } = useUpdateAutomation();
  const { automation: automationFromNetwork, isAutomationInitialLoading } = useLoadAutomation({
    enabled: isDefined(id),
    automationId: id!,
  });

  const isEditMode = isDefined(id);
  useEffect(() => {
    if (!isEditMode) return;
    if (isNullish(automationFromNetwork)) return;
    if (form.isDirty()) return;

    form.setValues(automationFromNetwork);
    form.resetDirty();
  }, [automationFromNetwork]);

  const cancelWizard = () => {
    setState(draft => Object.assign(draft, getAutomationWizardInitialState()));
    history.goBack();
  };

  const goToStep = (newStep: AutomationWizardStep) =>
    setState(({ step: draftStep }) => {
      draftStep.current = newStep;
      if (newStep > step.highestVisited) draftStep.highestVisited = newStep;
    });

  const goToNextStep = () => goToStep((step.current + 1) as AutomationWizardStep);

  const isStep1Valid = () => !isEmpty(automation.name) && isNullish(getNameFieldProps().error);

  const isStep2Valid = () => !isEmpty(automation.triggers);

  const isStep3Valid = () => {
    const { conditions } = automation;
    const { isConditionValid, isConditionEmpty, isConditionValidOrEmpty } = conditionsController;
    if (isEmpty(conditions)) return false;
    if (conditions.length === 1) return isConditionValid(first(conditions)!);
    if (conditions.every(isConditionEmpty)) return false;
    return conditions.every(isConditionValidOrEmpty);
  };

  const isStep4Valid = () => {
    const { actions } = automation;
    const { isActionValid, isActionEmpty, isActionValidOrEmpty } = actionsController;
    if (isEmpty(actions)) return false;
    if (actions.length === 1) return isActionValid(first(actions)!);
    if (actions.every(isActionEmpty)) return false;
    return actions.every(isActionValidOrEmpty);
  };

  const isStepAccessible = (stepIndex: number) => {
    if (stepIndex === 0) return true;
    if (stepIndex === 1) return isStep1Valid();
    if (stepIndex === 2) return isStep1Valid() && isStep2Valid();
    if (stepIndex === 3) return isStep1Valid() && isStep2Valid() && isStep3Valid();
    return false;
  };

  const isAutomationValid = () =>
    isStep1Valid() && isStep2Valid() && isStep3Valid() && isStep4Valid();

  const handleSave = () => {
    if (isEditMode)
      updateAutomation(markForDestroy(automationFromNetwork, automation), {
        onSuccess: cancelWizard,
      });
    else createAutomation(automation, { onSuccess: cancelWizard });
  };

  return {
    cancelWizard,
    handleSave,
    goToNextStep,
    goToStep,
    isStep1Valid,
    isStep2Valid,
    isStep3Valid,
    isStep4Valid,
    isStepAccessible,
    isAutomationValid,
    isCreateAutomationLoading,
    isEditMode,
    isWizardLoading: isAutomationInitialLoading,
    step,
  };
}
